import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/Etl/pipelines';

/* ─── Sabitler ─────────────────────────────────────────────── */
const SOURCE_TYPES = [
  { value: 1, label: 'API' },
  { value: 2, label: 'Veritabanı' },
  { value: 3, label: 'Excel' },
  { value: 4, label: 'JSON' },
];

const QUERY_TYPES = [
  { value: 1, label: 'Ham SQL' },
  { value: 2, label: 'Saklı Yordam' },
  { value: 3, label: 'View' },
];

const JSON_MODES = [
  { value: 1, label: 'Dosya' },
  { value: 2, label: 'Ham JSON' },
];

const TRANSFORM_TYPES = [
  { value: 1, label: 'Temizle' },
  { value: 2, label: 'Formatla' },
  { value: 3, label: 'Esle' },
  { value: 4, label: 'Filtrele' },
];

const FILTER_OPERATORS = [
  { value: 'eq', label: 'Esittir' },
  { value: 'ne', label: 'Esit degildir' },
  { value: 'gt', label: 'Buyuktur' },
  { value: 'gte', label: 'Buyuk veya esittir' },
  { value: 'lt', label: 'Kucuktur' },
  { value: 'lte', label: 'Kucuk veya esittir' },
  { value: 'contains', label: 'Icerir' },
  { value: 'notContains', label: 'Icermez' },
  { value: 'isNull', label: 'Bos/null' },
  { value: 'notNull', label: 'Bos/null degil' },
  { value: 'notEmpty', label: 'Bos metin degil' },
];

const CASING_OPTIONS = [
  { value: '', label: 'Degistirme' },
  { value: 'upper', label: 'Buyuk harf' },
  { value: 'lower', label: 'Kucuk harf' },
  { value: 'title', label: 'Bas harfleri buyuk' },
];

const STATUS_MAP = {
  0: { label: 'Hiç Çalışmadı', tone: 'neutral' },
  1: { label: 'Çalışıyor',      tone: 'wait' },
  2: { label: 'Başarılı',       tone: 'ok' },
  3: { label: 'Başarısız',      tone: 'bad' },
  4: { label: 'Kısmen Başarılı', tone: 'wait' },
};

const EMPTY_API    = { baseUrl: '', authHeaderName: '', authHeaderValue: '', paginationPath: '', nextPageUrlPath: '', timeoutSeconds: 30 };
const EMPTY_DB     = { queryType: 1, query: '', parameters: '{}', connectionStringName: '' };
const EMPTY_EXCEL  = { filePath: '', sheetName: '', headerRowIndex: 1, dataStartRowIndex: 2 };
const EMPTY_JSON   = { mode: 1, source: '', rootPath: '' };

const EMPTY_FORM = {
  id: '', name: '', description: '', sourceType: 1, cronExpression: '',
  transformStepsJson: '[]',
  api: { ...EMPTY_API },
  db: { ...EMPTY_DB },
  excel: { ...EMPTY_EXCEL },
  json: { ...EMPTY_JSON },
};

const DEFAULT_SCHEDULE = {
  mode: 'daily',
  everyMinutes: 15,
  minute: 0,
  hour: 8,
  dayOfWeek: 1,
  dayOfMonth: 1,
};

/* ─── Yardımcılar ───────────────────────────────────────────── */
function buildSourceConfig(form) {
  const sourceType = Number(form.sourceType);

  switch (sourceType) {
    case 1: return {
      sourceType: 1,
      baseUrl: form.api.baseUrl,
      authHeaderName: form.api.authHeaderName || null,
      authHeaderValue: form.api.authHeaderValue || null,
      paginationPath: form.api.paginationPath || null,
      nextPageUrlPath: form.api.nextPageUrlPath || null,
      timeoutSeconds: Number(form.api.timeoutSeconds) || 30,
    };
    case 2: {
      let params = {};
      try { params = JSON.parse(form.db.parameters || '{}'); } catch { params = {}; }
      return {
        sourceType: 2,
        queryType: Number(form.db.queryType),
        query: form.db.query,
        parameters: params,
        connectionStringName: form.db.connectionStringName || null,
      };
    }
    case 3: return {
      sourceType: 3,
      filePath: form.excel.filePath,
      sheetName: form.excel.sheetName || null,
      headerRowIndex: Number(form.excel.headerRowIndex) || 1,
      dataStartRowIndex: Number(form.excel.dataStartRowIndex) || 2,
    };
    case 4: return {
      sourceType: 4,
      mode: Number(form.json.mode),
      source: form.json.source,
      rootPath: form.json.rootPath || null,
    };
    default: return {};
  }
}

async function readApiError(response, fallback) {
  const text = await response.text().catch(() => '');
  if (!text) return fallback;

  try {
    const json = JSON.parse(text);
    if (json.message) return json.message;
    if (json.title) return json.title;
    if (json.errors && typeof json.errors === 'object') {
      return Object.entries(json.errors)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join(' | ');
    }
    return JSON.stringify(json);
  } catch {
    return text;
  }
}

function clampNumber(value, min, max, fallback) {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}

function buildCronExpression(schedule) {
  const minute = clampNumber(schedule.minute, 0, 59, 0);
  const hour = clampNumber(schedule.hour, 0, 23, 8);
  const everyMinutes = clampNumber(schedule.everyMinutes, 1, 59, 15);
  const dayOfWeek = clampNumber(schedule.dayOfWeek, 0, 6, 1);
  const dayOfMonth = clampNumber(schedule.dayOfMonth, 1, 31, 1);

  switch (schedule.mode) {
    case 'everyMinutes': return `*/${everyMinutes} * * * *`;
    case 'hourly': return `${minute} * * * *`;
    case 'weekly': return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly': return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'daily':
    default: return `${minute} ${hour} * * *`;
  }
}

function describeSchedule(schedule) {
  const minute = String(clampNumber(schedule.minute, 0, 59, 0)).padStart(2, '0');
  const hour = String(clampNumber(schedule.hour, 0, 23, 8)).padStart(2, '0');
  const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

  switch (schedule.mode) {
    case 'everyMinutes': return `Her ${clampNumber(schedule.everyMinutes, 1, 59, 15)} dakikada bir`;
    case 'hourly': return `Her saat ${minute}. dakikada`;
    case 'weekly': return `Her ${dayNames[clampNumber(schedule.dayOfWeek, 0, 6, 1)]} ${hour}:${minute}`;
    case 'monthly': return `Her ayın ${clampNumber(schedule.dayOfMonth, 1, 31, 1)}. günü ${hour}:${minute}`;
    case 'daily':
    default: return `Her gün ${hour}:${minute}`;
  }
}

function parseCronExpression(cron) {
  const text = (cron || '').trim();
  if (!text) return { ...DEFAULT_SCHEDULE };

  const parts = text.split(/\s+/);
  if (parts.length !== 5) return { ...DEFAULT_SCHEDULE };

  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
    return { ...DEFAULT_SCHEDULE, mode: 'everyMinutes', everyMinutes: clampNumber(minute.slice(2), 1, 59, 15) };
  }

  if (/^\d+$/.test(minute) && hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
    return { ...DEFAULT_SCHEDULE, mode: 'hourly', minute: clampNumber(minute, 0, 59, 0) };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && dayOfMonth === '*' && /^\d+$/.test(dayOfWeek)) {
    return {
      ...DEFAULT_SCHEDULE,
      mode: 'weekly',
      minute: clampNumber(minute, 0, 59, 0),
      hour: clampNumber(hour, 0, 23, 8),
      dayOfWeek: clampNumber(dayOfWeek, 0, 6, 1),
    };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && /^\d+$/.test(dayOfMonth) && dayOfWeek === '*') {
    return {
      ...DEFAULT_SCHEDULE,
      mode: 'monthly',
      minute: clampNumber(minute, 0, 59, 0),
      hour: clampNumber(hour, 0, 23, 8),
      dayOfMonth: clampNumber(dayOfMonth, 1, 31, 1),
    };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && dayOfMonth === '*' && dayOfWeek === '*') {
    return {
      ...DEFAULT_SCHEDULE,
      mode: 'daily',
      minute: clampNumber(minute, 0, 59, 0),
      hour: clampNumber(hour, 0, 23, 8),
    };
  }

  return { ...DEFAULT_SCHEDULE };
}

function parseTransformStepsJson(json) {
  try {
    const parsed = JSON.parse(json || '[]');
    if (!Array.isArray(parsed)) return { steps: [], error: 'Donusum adimlari dizi formatinda olmali.' };
    return { steps: normalizeTransformOrders(parsed), error: null };
  } catch (err) {
    return { steps: [], error: err.message };
  }
}

function normalizeTransformOrders(steps) {
  return steps.map((step, index) => {
    const stepType = Number(step?.stepType || 1);
    const options = step?.options && typeof step.options === 'object' && !Array.isArray(step.options)
      ? step.options
      : {};

    return {
      stepType,
      order: index + 1,
      options: normalizeTransformOptions(stepType, options),
    };
  });
}

function normalizeTransformOptions(stepType, options) {
  switch (Number(stepType)) {
    case 1: return {
      trimStrings: options.trimStrings !== false,
      nullifyEmpty: options.nullifyEmpty !== false,
      removeNullRows: options.removeNullRows === true,
      keepOnlyColumns: options.keepOnlyColumns === true,
      columns: arrayOfText(options.columns),
    };
    case 2: return {
      dateColumns: arrayOfText(options.dateColumns),
      dateFormat: scalarToText(options.dateFormat) || 'yyyy-MM-dd',
      numberColumns: arrayOfText(options.numberColumns),
      decimalPlaces: Number(scalarToText(options.decimalPlaces)) || 2,
      casingColumns: arrayOfText(options.casingColumns),
      casing: scalarToText(options.casing),
    };
    case 3: return {
      columns: normalizeTextMap(options.columns),
      values: normalizeNestedTextMap(options.values),
    };
    case 4: return {
      column: scalarToText(options.column),
      operator: scalarToText(options.operator) || 'eq',
      value: scalarToText(options.value),
    };
    default: return options;
  }
}

function stringifyTransformSteps(steps) {
  return JSON.stringify(normalizeTransformOrders(steps), null, 2);
}

function defaultOptionsForStep(stepType) {
  switch (Number(stepType)) {
    case 1: return { trimStrings: true, nullifyEmpty: true, removeNullRows: false };
    case 2: return { dateColumns: [], dateFormat: 'yyyy-MM-dd', numberColumns: [], decimalPlaces: 2, casingColumns: [], casing: '' };
    case 3: return { columns: {}, values: {} };
    case 4: return { column: '', operator: 'eq', value: '' };
    default: return {};
  }
}

function splitList(value) {
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

function listToText(value) {
  if (Array.isArray(value)) return value.map(scalarToText).filter(Boolean).join(', ');
  return scalarToText(value);
}

function scalarToText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function arrayOfText(value) {
  if (Array.isArray(value)) return value.map(scalarToText).filter(Boolean);
  const text = scalarToText(value);
  return text ? splitList(text) : [];
}

function normalizeTextMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, val]) => {
    const text = scalarToText(val);
    if (key && text) acc[key] = text;
    return acc;
  }, {});
}

function normalizeNestedTextMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, map]) => {
    const normalized = normalizeTextMap(map);
    if (key && Object.keys(normalized).length > 0) acc[key] = normalized;
    return acc;
  }, {});
}

function parseRenameMap(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .reduce((acc, line) => {
      const separatorIndex = line.indexOf('=');
      if (separatorIndex > 0) {
        const key = line.slice(0, separatorIndex).trim();
        const val = line.slice(separatorIndex + 1).trim();
        if (key && val) acc[key] = val;
      }
      return acc;
    }, {});
}

function renameMapToText(value) {
  if (!value || typeof value !== 'object') return '';
  return Object.entries(value)
    .map(([key, val]) => `${key}=${scalarToText(val)}`)
    .filter(line => !line.endsWith('='))
    .join('\n');
}

function parseValueMap(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .reduce((acc, line) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex <= 0) return acc;

      const column = line.slice(0, separatorIndex).trim();
      const pairs = line.slice(separatorIndex + 1).split(',');
      const map = {};

      pairs.forEach(pair => {
        const pairSeparator = pair.indexOf('=');
        if (pairSeparator <= 0) return;
        const from = pair.slice(0, pairSeparator).trim();
        const to = pair.slice(pairSeparator + 1).trim();
        if (from && to) map[from] = to;
      });

      if (column && Object.keys(map).length > 0) acc[column] = map;
      return acc;
    }, {});
}

function valueMapToText(value) {
  if (!value || typeof value !== 'object') return '';
  return Object.entries(value)
    .map(([column, map]) => {
      if (!map || typeof map !== 'object') return '';
      const pairs = Object.entries(map)
        .map(([from, to]) => `${from}=${scalarToText(to)}`)
        .filter(pair => !pair.endsWith('='))
        .join(', ');
      return pairs ? `${column}: ${pairs}` : '';
    })
    .filter(Boolean)
    .join('\n');
}

function mapPipelineToForm(pipeline) {
  const sourceConfig = pipeline?.sourceConfig || {};
  const transformSteps = Array.isArray(pipeline?.transformSteps) ? pipeline.transformSteps : [];

  return {
    id: pipeline?.id || '',
    name: pipeline?.name || '',
    description: pipeline?.description || '',
    sourceType: Number(pipeline?.sourceType || 1),
    cronExpression: pipeline?.cronExpression || '',
    transformStepsJson: JSON.stringify(transformSteps, null, 2),
    api: {
      ...EMPTY_API,
      baseUrl: sourceConfig.baseUrl || '',
      authHeaderName: sourceConfig.authHeaderName || '',
      authHeaderValue: sourceConfig.authHeaderValue || '',
      paginationPath: sourceConfig.paginationPath || '',
      nextPageUrlPath: sourceConfig.nextPageUrlPath || '',
      timeoutSeconds: sourceConfig.timeoutSeconds ?? 30,
    },
    db: {
      ...EMPTY_DB,
      queryType: sourceConfig.queryType ?? 1,
      query: sourceConfig.query || '',
      parameters: JSON.stringify(sourceConfig.parameters || {}, null, 2),
      connectionStringName: sourceConfig.connectionStringName || '',
    },
    excel: {
      ...EMPTY_EXCEL,
      filePath: sourceConfig.filePath || '',
      sheetName: sourceConfig.sheetName || '',
      headerRowIndex: sourceConfig.headerRowIndex ?? 1,
      dataStartRowIndex: sourceConfig.dataStartRowIndex ?? 2,
    },
    json: {
      ...EMPTY_JSON,
      mode: sourceConfig.mode ?? 1,
      source: sourceConfig.source || '',
      rootPath: sourceConfig.rootPath || '',
    },
  };
}

const fmtDate = (d) => d ? new Date(d).toLocaleString('tr-TR') : '—';

/* ─── Bileşen ───────────────────────────────────────────────── */
export function EtlPipelineDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni ETL Pipeline' : 'ETL Pipeline Detayı', "Veri aktarım pipeline'ını tanımlayın, aktifleştirin ve çalıştırın.");

  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  const [meta, setMeta] = useState(null); // lastStatus, lastRunAt, isActive, lastJobId — başlık/aksiyon için
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Cron zamanlama alt-modalı
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState(DEFAULT_SCHEDULE);

  // Log alt-modalı
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const setF = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setApi   = (key, val) => setForm(f => ({ ...f, api:   { ...f.api,   [key]: val } }));
  const setDb    = (key, val) => setForm(f => ({ ...f, db:    { ...f.db,    [key]: val } }));
  const setExcel = (key, val) => setForm(f => ({ ...f, excel: { ...f.excel, [key]: val } }));
  const setJson  = (key, val) => setForm(f => ({ ...f, json:  { ...f.json,  [key]: val } }));

  const transformConfig = useMemo(
    () => parseTransformStepsJson(form.transformStepsJson),
    [form.transformStepsJson]
  );

  // Tek kayıt yükleme: önce location.state.row ile başlık/meta, sonra GetById ile tüm detay.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    const row = location.state?.row;
    if (row) {
      setMeta({ lastStatus: row.lastStatus, lastRunAt: row.lastRunAt, isActive: row.isActive, lastJobId: row.lastJobId });
      setForm((c) => ({ ...c, id: row.id, name: row.name || '', sourceType: Number(row.sourceType || 1), cronExpression: row.cronExpression || '' }));
    }
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      const data = json.data || json;
      setForm(mapPipelineToForm(data));
      setMeta({ lastStatus: data.lastStatus, lastRunAt: data.lastRunAt, isActive: data.isActive, lastJobId: data.lastJobId });
      setAudit({ createdBy: data.createdBy ?? data.CreatedBy ?? data.createdUser ?? data.CreatedUser, createdDate: data.createdDate ?? data.CreatedDate, updatedBy: data.updatedBy ?? data.UpdatedBy ?? data.modifiedUser ?? data.ModifiedUser, updatedDate: data.updatedDate ?? data.UpdatedDate ?? data.modifiedDate ?? data.ModifiedDate });
    } catch (err) {
      showToast('error', `Pipeline detayi yuklenemedi: ${err.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /etl-pipelines/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm({ ...location.state.clone, id: '' });
      setMeta(null);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /* ── Cron zamanlama ── */
  const openScheduleModal = () => { setScheduleDraft(parseCronExpression(form.cronExpression)); setScheduleModalOpen(true); };
  const applySchedule = () => { setF('cronExpression', buildCronExpression(scheduleDraft)); setScheduleModalOpen(false); };

  /* ── Loglar (liste/detay aksiyonu — alt-modal) ── */
  const openLogs = async () => {
    if (isNew || !form.id) return;
    setLogsModalOpen(true);
    setLogsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${form.id}/logs?top=50`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setLogs(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      showToast('error', `Loglar yüklenemedi: ${err.message}`);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  /* ── Dönüşüm adımları builder ── */
  const setTransformSteps = (steps) => setF('transformStepsJson', stringifyTransformSteps(steps));

  const addTransformStep = () => {
    const steps = transformConfig.error ? [] : transformConfig.steps;
    setTransformSteps([...steps, { stepType: 1, order: steps.length + 1, options: defaultOptionsForStep(1) }]);
  };
  const removeTransformStep = (index) => setTransformSteps(transformConfig.steps.filter((_, i) => i !== index));
  const moveTransformStep = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= transformConfig.steps.length) return;
    const steps = [...transformConfig.steps];
    [steps[index], steps[nextIndex]] = [steps[nextIndex], steps[index]];
    setTransformSteps(steps);
  };
  const updateTransformStep = (index, patch) =>
    setTransformSteps(transformConfig.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  const updateTransformOption = (index, key, value) =>
    setTransformSteps(transformConfig.steps.map((step, i) =>
      i === index ? { ...step, options: { ...(step.options || {}), [key]: value } } : step));

  /* ── Doğrulama + body ── */
  const validate = () => {
    if (!form.name.trim()) { showToast('error', 'Pipeline adı zorunludur'); return false; }
    if (form.sourceType === 1 && !form.api.baseUrl.trim()) { showToast('error', 'API URL zorunludur'); return false; }
    if (form.sourceType === 2 && !form.db.query.trim()) { showToast('error', 'Sorgu / SP adı zorunludur'); return false; }
    if (form.sourceType === 3 && !form.excel.filePath.trim()) { showToast('error', 'Dosya yolu zorunludur'); return false; }
    if (form.sourceType === 4 && !form.json.source.trim()) { showToast('error', 'Kaynak zorunludur'); return false; }
    return true;
  };

  const buildBody = () => {
    let transformSteps;
    try { transformSteps = JSON.parse(form.transformStepsJson || '[]'); }
    catch { return null; }
    return {
      name: form.name.trim(),
      description: form.description.trim() || null,
      sourceType: Number(form.sourceType),
      sourceConfig: buildSourceConfig(form),
      transformSteps,
      cronExpression: form.cronExpression.trim() || null,
    };
  };

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan. Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!validate()) return;
    const body = buildBody();
    if (!body) { showToast('error', 'Dönüşüm adımları geçerli JSON değil'); return; }

    setSaving(true);
    try {
      const editing = !isNew && form.id;
      const res = await fetch(editing ? `${API_BASE}/${form.id}` : API_BASE, {
        method: editing ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await readApiError(res, editing ? 'Kayıt güncellenemedi' : 'Kayıt eklenemedi'));
      await res.json().catch(() => ({}));
      if (isNew) {
        if (andNew) {
          showToast('success', 'ETL Pipeline oluşturuldu. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setMeta(null);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'ETL Pipeline oluşturuldu');
        setTimeout(() => navigate('/etl-pipelines'), 400);
        return;
      }
      showToast('success', 'ETL Pipeline güncellendi');
      setTimeout(() => navigate('/etl-pipelines'), 400);
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/etl-pipelines/new', { state: { clone: { ...form, id: '' } } });
  };

  const handleDelete = async () => {
    if (!form.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${form.id}`, { method: 'DELETE', headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Silme başarısız');
      showToast('success', 'ETL Pipeline silindi');
      setTimeout(() => navigate('/etl-pipelines'), 400);
    } catch (err) {
      showToast('error', err.message);
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  /* ── Domaine özel aksiyonlar: Aktifleştir / Şimdi Çalıştır ── */
  const handleActivate = async () => {
    if (!form.id) return;
    try {
      const res = await fetch(`${API_BASE}/${form.id}/activate`, { method: 'POST', headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Aktifleştirme başarısız');
      showToast('success', `Pipeline aktifleştirildi (job: ${json.data || '—'})`);
      load();
    } catch (err) {
      showToast('error', err.message);
    }
  };

  const handleRun = async () => {
    if (!form.id) return;
    try {
      const res = await fetch(`${API_BASE}/${form.id}/run`, { method: 'POST', headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Çalıştırma başarısız');
      showToast('success', `Pipeline kuyruğa alındı (id: ${json.data || '—'})`);
      load();
    } catch (err) {
      showToast('error', err.message);
    }
  };

  const statusBadge = !isNew && meta && (() => {
    const st = STATUS_MAP[meta.lastStatus] ?? STATUS_MAP[0];
    return <StatusBadge tone={st.tone}>{st.label}</StatusBadge>;
  })();

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'layers' },
    { key: 'transform', label: 'Dönüşüm Adımları', icon: 'list' },
  ];

  const canActivate = !isNew && meta && !meta.isActive && !!form.cronExpression;

  const actions = (
    <>
      {!isNew && <button className="dps-btn ghost" onClick={openLogs} disabled={saving}><Icon name="list" size={16} /> Loglar</button>}
      {/* Aktifleştir/Çalıştır pipeline'ı tetikler (veri yazar) — Düzenle hakkına bağlıdır. */}
      {!isNew && canEdit && canActivate && <button className="dps-btn ghost" onClick={handleActivate} disabled={saving}>Aktifleştir</button>}
      {!isNew && canEdit && <button className="dps-btn ghost" onClick={handleRun} disabled={saving}>Şimdi Çalıştır</button>}
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/etl-pipelines')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          // Klonla yeni kayıt doğurur → hem Kopyala hem Yeni hakkı ister.
          menu={canCopy && canAdd ? [{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni pipeline aç',
            icon: 'copy',
            onClick: handleClone,
          }] : []}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'ETL Pipeline Tanımları', to: '/etl-pipelines' }, { label: isNew ? 'Yeni Pipeline' : 'Pipeline Detayı', current: true }]}
      status={statusBadge}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'EtlPipeline', entityId: id }}
      loading={loading}
    >
      {activeTab === 'general' && (
        <div className="dps-form">
          <Section icon="layers" title="Temel Bilgiler" subtitle="Pipeline adı, kaynak tipi ve zamanlama">
            <Grid cols={2}>
              <FieldText form={form} set={setF} tried={tried} k="name" label="Pipeline Adı" req placeholder="Örn: Müşteri Veri Aktarımı" />
              <FieldText form={form} set={setF} k="description" label="Açıklama" placeholder="İsteğe bağlı açıklama" />
              <FieldSelect
                form={form}
                set={(k, v) => setF(k, Number(v))}
                k="sourceType" label="Kaynak Tipi" req
                options={SOURCE_TYPES}
              />
              <FieldCustom label="Cron Zamanlama">
                <div style={styles.cronFieldWrap}>
                  <input
                    style={styles.cronInput}
                    value={form.cronExpression}
                    onChange={(e) => setF('cronExpression', e.target.value)}
                    placeholder="Orn: 0 8 * * *"
                  />
                  <button type="button" style={styles.cronBuilderButton} onClick={openScheduleModal}>Oluştur</button>
                </div>
                <div style={styles.cronHint}>Dakika Saat Gun Ay HaftaGunu biciminde cron degeri.</div>
              </FieldCustom>
            </Grid>
          </Section>

          {/* Kaynak tipine göre değişken bölümler */}
          {form.sourceType === 1 && (
            <Section icon="box" title="API Kaynağı" subtitle="HTTP uç noktası ve kimlik doğrulama">
              <Grid cols={2}>
                <FieldText form={form.api} set={setApi} tried={tried} k="baseUrl" label="URL" req placeholder="https://api.example.com/data" span />
                <FieldText form={form.api} set={setApi} k="authHeaderName" label="Auth Header Adı" placeholder="Authorization" />
                <FieldText form={form.api} set={setApi} k="authHeaderValue" label="Auth Header Değeri" placeholder="Bearer token..." />
                <FieldText form={form.api} set={setApi} k="paginationPath" label="Veri Dizisi JSONPath" placeholder="$.data" />
                <FieldText form={form.api} set={setApi} k="nextPageUrlPath" label="Sonraki Sayfa JSONPath" placeholder="$.next_page" />
                <FieldText form={form.api} set={setApi} k="timeoutSeconds" label="Timeout (s)" num />
              </Grid>
            </Section>
          )}

          {form.sourceType === 2 && (
            <Section icon="box" title="Veritabanı Kaynağı" subtitle="Sorgu / saklı yordam ve parametreler">
              <Grid cols={2}>
                <FieldSelect form={form.db} set={(k, v) => setDb(k, Number(v))} k="queryType" label="Sorgu Tipi" options={QUERY_TYPES} />
                <FieldText form={form.db} set={setDb} k="connectionStringName" label="Bağlantı Adı" placeholder="Boş = uygulama DB" />
                <FieldTextarea form={form.db} set={setDb} k="query" label="Sorgu / SP Adı" rows={4} placeholder="SELECT * FROM [User] WHERE IsActive = 1" span />
                <FieldText form={form.db} set={setDb} k="parameters" label="Parametreler (JSON)" mono placeholder='{"TopN": 100}' span />
              </Grid>
            </Section>
          )}

          {form.sourceType === 3 && (
            <Section icon="box" title="Excel Kaynağı" subtitle="Dosya yolu ve sayfa konumu">
              <Grid cols={2}>
                <FieldText form={form.excel} set={setExcel} tried={tried} k="filePath" label="Dosya Yolu" req placeholder="C:\Imports\data.xlsx" span />
                <FieldText form={form.excel} set={setExcel} k="sheetName" label="Sayfa Adı" placeholder="Boş = ilk sayfa" />
                <FieldText form={form.excel} set={setExcel} k="headerRowIndex" label="Başlık Satırı" num />
                <FieldText form={form.excel} set={setExcel} k="dataStartRowIndex" label="Veri Başlangıç" num />
              </Grid>
            </Section>
          )}

          {form.sourceType === 4 && (
            <Section icon="box" title="JSON Kaynağı" subtitle="Dosya veya ham JSON metni">
              <Grid cols={2}>
                <FieldSelect form={form.json} set={(k, v) => setJson(k, Number(v))} k="mode" label="Mod" options={JSON_MODES} />
                <FieldText
                  form={form.json} set={setJson} tried={tried} k="source" req
                  label={form.json.mode === 1 ? 'Dosya Yolu' : 'Ham JSON'}
                  placeholder={form.json.mode === 1 ? 'C:\\Imports\\data.json' : '[{"id":1}]'}
                />
                <FieldText form={form.json} set={setJson} k="rootPath" label="Kök Dizi JSONPath" placeholder="$.data.items" span />
              </Grid>
            </Section>
          )}
        </div>
      )}

      {activeTab === 'transform' && (
        <div className="dps-form">
          <Section
            icon="list"
            title="Dönüşüm Adımları"
            subtitle="Aktarım sırasında uygulanacak veri dönüşümleri"
            right={<button className="dps-blk-add" onClick={addTransformStep}><Icon name="plus" size={15} /> Adım Ekle</button>}
          >
            {transformConfig.error && (
              <div style={styles.transformError}>
                JSON okunamadi: {transformConfig.error}. Yeni adim eklemek mevcut JSON'u temizleyip form formatina alir.
              </div>
            )}

            {!transformConfig.error && transformConfig.steps.length === 0 && (
              <div className="dps-empty"><Icon name="list" size={34} /><p>Henüz dönüşüm adımı yok</p><span>Sağ üstten “Adım Ekle” ile adım oluşturun. Veri olduğu gibi aktarılır.</span></div>
            )}

            {!transformConfig.error && transformConfig.steps.map((step, index) => (
              <div key={`${step.order}-${index}`} style={styles.transformStep}>
                <div style={styles.transformStepHeader}>
                  <div style={styles.stepBadge}>{index + 1}</div>
                  <select
                    style={styles.stepSelect}
                    value={step.stepType}
                    onChange={(e) => {
                      const stepType = Number(e.target.value);
                      updateTransformStep(index, { stepType, options: defaultOptionsForStep(stepType) });
                    }}
                  >
                    {TRANSFORM_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                  </select>
                  <button type="button" style={styles.iconButton} onClick={() => moveTransformStep(index, -1)} disabled={index === 0}>Yukari</button>
                  <button type="button" style={styles.iconButton} onClick={() => moveTransformStep(index, 1)} disabled={index === transformConfig.steps.length - 1}>Asagi</button>
                  <button type="button" style={styles.removeButton} onClick={() => removeTransformStep(index)}>Sil</button>
                </div>
                <TransformStepOptions step={step} index={index} onOptionChange={updateTransformOption} />
              </div>
            ))}

            <details style={styles.advancedJson}>
              <summary style={styles.advancedSummary}>Gelismis JSON</summary>
              <textarea
                style={styles.jsonTextarea}
                value={form.transformStepsJson}
                onChange={(e) => setF('transformStepsJson', e.target.value)}
                placeholder='[{"stepType":1,"order":1,"options":{"trimStrings":true}}]'
              />
            </details>
          </Section>
        </div>
      )}

      {/* Cron zamanlama alt-modalı */}
      {scheduleModalOpen && (
        <SubModal
          title="Cron Zamanlama Oluştur"
          maxWidth={560}
          onClose={() => setScheduleModalOpen(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setScheduleModalOpen(false)}>İptal</button>
              <button className="dps-btn primary" onClick={applySchedule}>Uygula</button>
            </>
          )}
        >
          <div style={styles.scheduleIntro}>
            Zamanlamayi kullanici dostu alanlardan secin. Uygula dediginizde olusan cron metni ilgili sahaya yazilir.
          </div>

          <FieldSelect
            form={scheduleDraft}
            set={(k, v) => setScheduleDraft((c) => ({ ...c, [k]: v }))}
            k="mode" label="Zamanlama Tipi"
            options={[
              { value: 'everyMinutes', label: 'Her X dakikada bir' },
              { value: 'hourly', label: 'Her saat' },
              { value: 'daily', label: 'Her gun' },
              { value: 'weekly', label: 'Haftalik' },
              { value: 'monthly', label: 'Aylik' },
            ]}
          />

          {scheduleDraft.mode === 'everyMinutes' && (
            <FieldText form={scheduleDraft} set={(k, v) => setScheduleDraft((c) => ({ ...c, [k]: v }))} k="everyMinutes" label="Dakika Araligi" num />
          )}
          {scheduleDraft.mode === 'hourly' && (
            <FieldText form={scheduleDraft} set={(k, v) => setScheduleDraft((c) => ({ ...c, [k]: v }))} k="minute" label="Her saatin kacinci dakikasinda?" num />
          )}
          {(scheduleDraft.mode === 'daily' || scheduleDraft.mode === 'weekly' || scheduleDraft.mode === 'monthly') && (
            <Grid cols={2}>
              <FieldText form={scheduleDraft} set={(k, v) => setScheduleDraft((c) => ({ ...c, [k]: v }))} k="hour" label="Saat" num />
              <FieldText form={scheduleDraft} set={(k, v) => setScheduleDraft((c) => ({ ...c, [k]: v }))} k="minute" label="Dakika" num />
            </Grid>
          )}
          {scheduleDraft.mode === 'weekly' && (
            <FieldSelect
              form={scheduleDraft}
              set={(k, v) => setScheduleDraft((c) => ({ ...c, [k]: v }))}
              k="dayOfWeek" label="Haftanin Gunu"
              options={[
                { value: '0', label: 'Pazar' }, { value: '1', label: 'Pazartesi' }, { value: '2', label: 'Sali' },
                { value: '3', label: 'Carsamba' }, { value: '4', label: 'Persembe' }, { value: '5', label: 'Cuma' },
                { value: '6', label: 'Cumartesi' },
              ]}
            />
          )}
          {scheduleDraft.mode === 'monthly' && (
            <FieldText form={scheduleDraft} set={(k, v) => setScheduleDraft((c) => ({ ...c, [k]: v }))} k="dayOfMonth" label="Ayin Kacinci Gunu" num />
          )}

          <div style={styles.schedulePreviewBox}>
            <div style={styles.schedulePreviewLabel}>Onizleme</div>
            <div style={styles.schedulePreviewText}>{describeSchedule(scheduleDraft)}</div>
            <code style={styles.schedulePreviewCode}>{buildCronExpression(scheduleDraft)}</code>
          </div>
        </SubModal>
      )}

      {/* Log alt-modalı */}
      {logsModalOpen && (
        <SubModal
          title={`Çalışma Logları — ${form.name}`}
          maxWidth={760}
          onClose={() => setLogsModalOpen(false)}
          footer={<button className="dps-btn ghost" onClick={() => setLogsModalOpen(false)}>Kapat</button>}
        >
          {logsLoading ? (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Yükleniyor...</p>
          ) : logs.length === 0 ? (
            <div className="dps-empty"><Icon name="list" size={34} /><p>Henüz çalışma kaydı yok</p></div>
          ) : (
            <table className="dps-table">
              <thead>
                <tr><th>Başlangıç</th><th>Bitiş</th><th>Durum</th><th>Okunan</th><th>Yazılan</th><th>Hata</th></tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const st = STATUS_MAP[log.status] ?? STATUS_MAP[0];
                  return (
                    <tr key={log.id}>
                      <td>{fmtDate(log.startedAt)}</td>
                      <td>{fmtDate(log.completedAt)}</td>
                      <td><StatusBadge tone={st.tone}>{st.label}</StatusBadge></td>
                      <td>{log.rowsRead ?? '—'}</td>
                      <td>{log.rowsWritten ?? '—'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.errorMessage}>
                        {log.errorMessage || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </SubModal>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> pipeline kaydını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

/* ─── Dönüşüm adımı seçenekleri ──────────────────────────────── */
function TransformStepOptions({ step, index, onOptionChange }) {
  const options = step.options || {};

  if (step.stepType === 1) {
    return (
      <div style={styles.optionGrid}>
        <CheckboxField label="Bosluklari kirp" checked={options.trimStrings !== false} onChange={value => onOptionChange(index, 'trimStrings', value)} />
        <CheckboxField label="Bos metinleri null yap" checked={options.nullifyEmpty !== false} onChange={value => onOptionChange(index, 'nullifyEmpty', value)} />
        <CheckboxField label="Tamamen bos satirlari sil" checked={!!options.removeNullRows} onChange={value => onOptionChange(index, 'removeNullRows', value)} />
        <CheckboxField label="Sadece bu kolonlari tut" checked={!!options.keepOnlyColumns} onChange={value => onOptionChange(index, 'keepOnlyColumns', value)} />
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={styles.fieldLabel}>Kolonlar</label>
          <input
            style={styles.fieldInput}
            value={listToText(options.columns)}
            onChange={e => onOptionChange(index, 'columns', splitList(e.target.value))}
            placeholder="Bos birakilirsa tum kolonlar. Or: Name, City"
          />
        </div>
      </div>
    );
  }

  if (step.stepType === 2) {
    return (
      <div style={styles.optionGrid}>
        <div>
          <label style={styles.fieldLabel}>Tarih Kolonlari</label>
          <input style={styles.fieldInput} value={listToText(options.dateColumns)} onChange={e => onOptionChange(index, 'dateColumns', splitList(e.target.value))} placeholder="OrderDate, CreatedAt" />
        </div>
        <div>
          <label style={styles.fieldLabel}>Tarih Formati</label>
          <input style={styles.fieldInput} value={scalarToText(options.dateFormat) || 'yyyy-MM-dd'} onChange={e => onOptionChange(index, 'dateFormat', e.target.value)} placeholder="yyyy-MM-dd" />
        </div>
        <div>
          <label style={styles.fieldLabel}>Sayi Kolonlari</label>
          <input style={styles.fieldInput} value={listToText(options.numberColumns)} onChange={e => onOptionChange(index, 'numberColumns', splitList(e.target.value))} placeholder="Amount, Total" />
        </div>
        <div>
          <label style={styles.fieldLabel}>Ondalik Basamak</label>
          <input style={styles.fieldInput} type="number" min="0" value={scalarToText(options.decimalPlaces) || 2} onChange={e => onOptionChange(index, 'decimalPlaces', Number(e.target.value) || 0)} />
        </div>
        <div>
          <label style={styles.fieldLabel}>Harf Formati Kolonlari</label>
          <input style={styles.fieldInput} value={listToText(options.casingColumns)} onChange={e => onOptionChange(index, 'casingColumns', splitList(e.target.value))} placeholder="Name, City" />
        </div>
        <div>
          <label style={styles.fieldLabel}>Harf Formati</label>
          <select style={styles.fieldInput} value={scalarToText(options.casing)} onChange={e => onOptionChange(index, 'casing', e.target.value)}>
            {CASING_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      </div>
    );
  }

  if (step.stepType === 3) {
    return (
      <div style={styles.optionGrid}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={styles.fieldLabel}>Kolon Adi Degistir</label>
          <MappingTextarea
            value={renameMapToText(options.columns)}
            onCommit={value => onOptionChange(index, 'columns', parseRenameMap(value))}
            placeholder={'OldName=NewName\nCustomerCode=Code'}
          />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={styles.fieldLabel}>Deger Esle</label>
          <MappingTextarea
            value={valueMapToText(options.values)}
            onCommit={value => onOptionChange(index, 'values', parseValueMap(value))}
            placeholder={'Status: 1=Active, 0=Inactive\nType: C=Customer, V=Vendor'}
          />
        </div>
      </div>
    );
  }

  if (step.stepType === 4) {
    const operator = scalarToText(options.operator) || 'eq';
    const needsValue = !['isNull', 'notNull', 'notEmpty'].includes(operator);
    return (
      <div style={styles.optionGrid}>
        <div>
          <label style={styles.fieldLabel}>Kolon</label>
          <input style={styles.fieldInput} value={scalarToText(options.column)} onChange={e => onOptionChange(index, 'column', e.target.value)} placeholder="Status" />
        </div>
        <div>
          <label style={styles.fieldLabel}>Operator</label>
          <select style={styles.fieldInput} value={operator} onChange={e => onOptionChange(index, 'operator', e.target.value)}>
            {FILTER_OPERATORS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        {needsValue && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={styles.fieldLabel}>Karsilastirma Degeri</label>
            <input style={styles.fieldInput} value={scalarToText(options.value)} onChange={e => onOptionChange(index, 'value', e.target.value)} placeholder="Active" />
          </div>
        )}
      </div>
    );
  }

  return null;
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label style={styles.checkboxField}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function MappingTextarea({ value, onCommit, placeholder }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <textarea
      style={{ ...styles.fieldInput, height: 76, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      placeholder={placeholder}
    />
  );
}

const styles = {
  cronFieldWrap: { display: 'flex', gap: 10, alignItems: 'center' },
  cronInput: {
    flex: 1, height: 42, padding: '0 12px', borderRadius: 12,
    border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-primary)',
    fontSize: 14,
  },
  cronBuilderButton: {
    flexShrink: 0, height: 42, padding: '0 14px', borderRadius: 12,
    border: '1px solid #0073ea33', background: '#0073ea12', color: 'var(--accent)',
    fontWeight: 700, cursor: 'pointer',
  },
  cronHint: { marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' },
  fieldLabel: {
    display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)',
  },
  fieldInput: {
    width: '100%', boxSizing: 'border-box', height: 42, padding: '0 12px', borderRadius: 12,
    border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-primary)',
    fontSize: 14,
  },
  transformError: {
    padding: '10px 12px', borderRadius: 8, border: '1px solid #E53E3E33',
    background: '#E53E3E12', color: '#dc2626', fontSize: 13, marginBottom: 12,
  },
  transformStep: {
    border: '1px solid var(--border-color)', borderRadius: 8, padding: 12,
    background: 'var(--input-bg)', marginBottom: 12,
  },
  transformStepHeader: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 },
  stepSelect: {
    flex: 1, height: 36, padding: '0 10px', borderRadius: 8,
    border: '1px solid var(--border-color)', background: 'var(--modal-bg)', color: 'var(--text-primary)',
  },
  stepBadge: {
    width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, background: '#0073ea12', color: 'var(--accent)', fontWeight: 800, fontSize: 13,
  },
  iconButton: {
    height: 36, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border-color)',
    background: 'var(--modal-bg)', color: 'var(--text-secondary)', fontWeight: 700, cursor: 'pointer',
  },
  removeButton: {
    height: 36, padding: '0 10px', borderRadius: 8, border: '1px solid #E53E3E33',
    background: '#E53E3E12', color: '#dc2626', fontWeight: 700, cursor: 'pointer',
  },
  optionGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
  checkboxField: {
    display: 'flex', alignItems: 'center', gap: 8, minHeight: 42, padding: '0 10px', borderRadius: 8,
    border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
  },
  advancedJson: { borderTop: '1px solid var(--border-color)', paddingTop: 10, marginTop: 4 },
  advancedSummary: { cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 700 },
  jsonTextarea: {
    width: '100%', boxSizing: 'border-box', height: 120, resize: 'vertical', fontFamily: 'monospace', fontSize: 12,
    marginTop: 10, padding: 12, borderRadius: 12, border: '1px solid var(--border-color)',
    background: 'var(--input-bg)', color: 'var(--text-primary)',
  },
  scheduleIntro: {
    marginBottom: 14, padding: '12px 14px', borderRadius: 12, background: 'rgba(0,115,234,0.06)',
    color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5,
  },
  schedulePreviewBox: {
    marginTop: 8, padding: 16, borderRadius: 14, border: '1px solid var(--border-color)', background: 'var(--input-bg)',
  },
  schedulePreviewLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 6,
  },
  schedulePreviewText: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 },
  schedulePreviewCode: {
    display: 'inline-block', padding: '6px 10px', borderRadius: 10, background: '#0F172A', color: '#F8FAFC', fontSize: 13,
  },
};

export default EtlPipelineDetailPage;
